import { useUserInfo } from "@next-platform/client-common-core";
import { Text as SpiceUIText, TextVariants } from "@next-platform/client-common-spicy-ui";
import { Spacer } from "@nextui-org/react";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { Col, Row, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import useApi from "@/components/useApi";
import useFireConversionOnce from "@/features/tracking/useFireConversionOnce";
import { useMediaStore } from "@/hooks";

import buildCheckoutConversionInputs, { type ConversionInputs } from "./buildCheckoutConversionInputs";
import { PaymentCardHeader } from "./components/PaymentCardHeader";
import { PaymentDetailsCard } from "./components/PaymentDetailsCard";
import { StartChattingButton } from "./components/StartChattingButton";

const FORCE_REFRESH_TOKEN_DELAY = 5000;
const MOBILE_SPACING = 4;
const DESKTOP_SPACING = 12;
/** Hoisted out of JSX so the object identity is stable across renders. */
const UPDATE_DELAY_TRANS_VALUES = { minutes: 5 };

/**
 * The URL params are available on first paint while `onlyUserId` hydrates
 * async, and `useFireConversionOnce` latches after its first fire — so firing
 * before auth resolves would ship the event without `cw_client_user_id` and
 * break the CATTRNX join it exists for. `isAuthLoaded` flips whether or not
 * there is a user, so a logged-out success page still reports.
 */
function canReportConversion(conversionInputs: ConversionInputs | null, isAuthLoaded: boolean | undefined): boolean {
  return conversionInputs !== null && isAuthLoaded === true;
}

const CheckoutSuccess = () => {
  const { onlyUserId, isAuthLoaded } = useUserInfo();
  const { forceRefreshToken } = useApi();

  const [searchParams] = useSearchParams();
  const {
    orderTotal,
    netAmount,
    order_id = null,
    subscription_id = null,
    transactionID = null,
    plan,
    date,
    currency = "USD",
    plan_id,
    payment_system,
    customerId,
  } = Object.fromEntries(searchParams.entries());
  const isMobile = useMediaStore((store) => store.isMobile);

  useEffect(() => {
    const timeout = setTimeout(() => {
      // Fire-and-forget: the page renders the same either way, and a failed
      // refresh is retried by the next authenticated request.
      void forceRefreshToken();
    }, FORCE_REFRESH_TOKEN_DELAY);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; forceRefreshToken identity changes on Kinde response
  }, []);

  // The dedup key is `order_id` — the backend CAPI `event_id` resolves to the
  // Sticky order id, despite travelling through a server variable named
  // `transaction_id`. Drift breaks browser ↔ server dedup in Meta Ads.
  // `transactionID` is passed separately for CampaignsWell, which wants the
  // payment/transaction id. `subscription_id` distinguishes `Subscribe`
  // (first charge) from `Purchase` (one-shot); renewals fire from the backend
  // webhook only.
  const conversionInputs = buildCheckoutConversionInputs({
    orderId: order_id,
    transactionId: transactionID,
    orderTotal,
    netAmount,
    subscriptionId: subscription_id,
    currency,
    paymentSystem: payment_system,
    productId: plan_id,
    customerId,
    // `onlyUserId`, not `userId` — the latter falls back to the guest id,
    // which would never match the `user_id` on our CATTRNX postbacks.
    clientUserId: onlyUserId,
  });
  useFireConversionOnce(() => conversionInputs as ConversionInputs, canReportConversion(conversionInputs, isAuthLoaded));

  if (!onlyUserId) {
    return null;
  }

  return (
    <Row className={"justify-between px-12 py-12 w-full max-lg:px-0 " + (isMobile && "flex-col items-center pt-8")}>
      {isMobile ? <PaymentCardHeader currency={currency} orderTotal={orderTotal} /> : null}
      <Col className={"w-[750px] max-w-full " + (isMobile && "items-center text-center mt-6 p-2")}>
        <Spacer y={1} />
        <Text b variant={isMobile ? "xxl" : "biggest"}>
          <Trans i18nKey="subscription:checkoutSuccess.thankYou" />
        </Text>
        <Spacer y={6} />
        <SpiceUIText variant={TextVariants.LABEL_MEDIUM_BOLD}>
          <Trans i18nKey="subscription:checkoutSuccess.updateDelayMessage" values={UPDATE_DELAY_TRANS_VALUES} />
        </SpiceUIText>
        <Spacer y={6} />
        <Text variant={isMobile ? "md" : "xxl"} type={isMobile ? "text-start" : undefined}>
          <Trans i18nKey="subscription:checkoutSuccess.confirmationNote" />
        </Text>
        <Spacer y={isMobile ? MOBILE_SPACING : DESKTOP_SPACING} />
        {!isMobile && <StartChattingButton />}
      </Col>
      <PaymentDetailsCard
        currency={currency}
        orderTotal={orderTotal}
        plan={plan}
        date={date}
        order_id={order_id}
        subscription_id={subscription_id}
        transactionID={transactionID}
      />
      {isMobile ? (
        <>
          <Spacer y={8} />
          <StartChattingButton />
        </>
      ) : null}
    </Row>
  );
};

export default CheckoutSuccess;
